// Destructive actions (redesign spec §4): the page-level Danger zone for the
// page's own entity, and the in-card DangerStrip for an item's own actions,
// shown only while that item is being edited.

import { Button } from "@pej/kore/components";
import type { ReactNode } from "react";

export interface DangerZoneSectionProps {
    id?: string;
    title?: string;
    description: ReactNode;
    action: string;
    /** Set when the action is refused up front; shown instead of offering a button that would fail. */
    disabledReason?: ReactNode;
    onAction: () => void;
}

export function DangerZoneSection({
    id = "section-danger",
    title = "Danger zone",
    description,
    action,
    disabledReason,
    onAction,
}: DangerZoneSectionProps) {
    return (
        <section
            id={id}
            className="flex scroll-mt-[4.5rem] flex-col gap-3 rounded-kore-md border border-kore-danger/30 bg-kore-danger-soft/40 p-5"
        >
            <h2 className="text-base font-semibold text-kore-danger-text">{title}</h2>
            <div className="flex items-center justify-between gap-4">
                <div className="text-sm text-kore-text-muted">
                    <p>{description}</p>
                    {disabledReason && <p className="mt-1 font-medium text-kore-text">{disabledReason}</p>}
                </div>
                <Button type="button" variant="destructive" size="sm" onClick={onAction} disabled={!!disabledReason}>
                    {action}
                </Button>
            </div>
        </section>
    );
}

/** An item's own destructive actions, at the foot of its card. Only while the
 *  card is being edited (spec §4) — view mode never shows a delete. */
export function DangerStrip({ isEditing, children }: { isEditing: boolean; children: ReactNode }) {
    if (!isEditing) return null;
    return (
        <div className="-mx-6 -mb-6 mt-6 flex flex-wrap items-center justify-end gap-2 rounded-b-kore-md border-t border-kore-danger/30 bg-kore-danger-soft/40 px-6 py-3">
            {children}
        </div>
    );
}
